/**
 * O contexto de tela que viaja em TODA mensagem do chat (US-5 da spec-001 do
 * `project-plataforma-por-chat`): `rota_atual` + `filtros_da_tela`.
 *
 * ★ Este módulo vive FORA de `api/` de propósito, e a rota chega por INJEÇÃO —
 * quem sabe qual tela está aberta é o componente, não a classe de API.
 *
 * Um `import router from '@router'` dentro de `api/apiChat.js` fecha um ciclo:
 * `@router` monta as rotas, `router/routes.js:801` carrega `_404.vue` de forma
 * EAGER, e de lá a cadeia vai a `layouts/main.vue` →
 * `fee-fixo-ia-especialista.vue` → `apiFeeFixoIa.js`, que faz
 * `class APIFeeFixoIa extends APIChat` no corpo do módulo. O `extends` passa
 * então a ler a classe antes de ela existir —
 * `ReferenceError: Cannot access 'APIChat' before initialization`, medido em 8
 * suítes em 26/08/2026. Injetar não é preferência de estilo: é a única forma de
 * a camada de API conhecer a tela sem entrar nesse grafo.
 *
 * ★ Por isso este arquivo é PURO — sem `import` de módulo, rota ou store — e
 * assim tem de ficar: é o que `api/apiChat.js` importa. O provedor que as
 * superfícies registram (`provedorDeContextoDeTela`, que lê a nav-bar dos
 * módulos via `secoes-do-modulo.js`) vive em `provedor-de-contexto-de-tela.js`
 * e é dos componentes. `contexto-de-tela.unit.js` reprova import de
 * `@modules/`/`@router` aqui.
 */

// Teto do contexto (US-5). Medido em BYTES do JSON serializado: o backend o
// guarda numa coluna JSONB, e uma tela com filtro de texto livre em PT-BR gasta
// mais bytes do que caracteres.
export const LIMITE_FILTROS_DA_TELA = 2048

/** O contexto de quem não registrou provedor: sem tela, e sem quebrar nada. */
export function contextoSemTela() {
  return { rota_atual: null, filtros_da_tela: {} }
}

function serializar(valor) {
  try {
    return JSON.stringify(valor)
  } catch (e) {
    // Ciclo ou BigInt no filtro da tela: o contexto é acessório, a mensagem não.
    return null
  }
}

/**
 * Bytes UTF-8 do texto, contados à mão.
 *
 * NÃO usa `TextEncoder`: ele existe no browser, mas não no jsdom da suíte — e
 * um fallback para `String.length` mediria CARACTERES, deixando o teste passar
 * por baixo do teto enquanto a produção estoura. Medido em 26/08/2026: 1.400
 * caracteres acentuados são 1.417 no `length` do JSON e 2.817 bytes.
 */
function tamanhoEmBytes(texto) {
  let bytes = 0
  for (let i = 0; i < texto.length; i++) {
    const codigo = texto.charCodeAt(i)
    if (codigo < 0x80) bytes += 1
    else if (codigo < 0x800) bytes += 2
    else if (codigo >= 0xd800 && codigo <= 0xdbff) {
      // Par surrogate (emoji): 2 unidades de código, 4 bytes.
      bytes += 4
      i++
    } else bytes += 3
  }
  return bytes
}

/**
 * Passou do teto? Os dois atalhos evitam varrer o texto no caso comum: um byte
 * por caractere é o piso, quatro é o topo. Só a faixa ambígua — que por
 * construção tem no máximo 2.048 caracteres — é contada de verdade.
 */
function excedeOTeto(texto) {
  if (texto.length > LIMITE_FILTROS_DA_TELA) return true
  if (texto.length * 4 <= LIMITE_FILTROS_DA_TELA) return false
  return tamanhoEmBytes(texto) > LIMITE_FILTROS_DA_TELA
}

/**
 * Corta os filtros no teto de 2 KB — por CHAVE, nunca por caractere.
 *
 * JSON cortado no meio não é JSON, e o destino é uma coluna JSONB. Entra o que
 * couber, na ordem em que a tela declarou, e `truncado: true` avisa que o resto
 * ficou de fora. A mensagem NUNCA é recusada por causa do contexto.
 */
export function cortarFiltrosDaTela(filtros) {
  const inteiro = serializar(filtros)
  if (inteiro !== null && !excedeOTeto(inteiro)) {
    return filtros
  }
  const cortado = { truncado: true }
  for (const chave of Object.keys(filtros)) {
    const tentativa = serializar({ ...cortado, [chave]: filtros[chave] })
    if (tentativa === null || excedeOTeto(tentativa)) break
    cortado[chave] = filtros[chave]
  }
  return cortado
}

/** A rota no shape que o backend registra no `reqparse` — ou `null`. */
export function normalizarRota(rota) {
  if (!rota) return null
  return {
    name: rota.name || null,
    params: rota.params || {},
    query: rota.query || {},
  }
}

/** Filtros sempre objeto, sempre dentro do teto. */
export function normalizarFiltros(filtros) {
  if (!filtros || typeof filtros !== 'object') return {}
  return cortarFiltrosDaTela(filtros)
}

/**
 * Monta o contexto no shape do corpo do `POST /chat`.
 *
 * @param {Object=} opts
 * @param {Object=} opts.rota     `$route` da superfície (ou equivalente).
 * @param {Object=} opts.filtros  O que a tela quer contar à IA.
 */
export function montarContextoDeTela({ rota, filtros } = {}) {
  return {
    rota_atual: normalizarRota(rota),
    filtros_da_tela: normalizarFiltros(filtros),
  }
}
